import { format } from 'date-fns/fp';

const moneyFormatter = new Intl.NumberFormat('en', { style: 'currency', currency: 'USD' });
const percentFormatter = new Intl.NumberFormat('en', {
    style: 'percent',
    minimumFractionDigits: 2,
    minimumIntegerDigits: 2,
});
const numberFormatter = new Intl.NumberFormat('en');
const zeroDecimalNumberFormatter = new Intl.NumberFormat('en', { maximumFractionDigits: 0 });

const zeroDecimalMoneyFormatter = new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
});
const fourDecimalMoneyFormatter = new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
});
const twoDecimalMoneyFormatter = new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
});

export const formatMoney = (value: number) => moneyFormatter.format(value);
export const formatFourDecimalMoney = (value: number) => fourDecimalMoneyFormatter.format(value);
export const formatTwoDecimalMoney = (value: number) => twoDecimalMoneyFormatter.format(value);

export const formatZeroDecimalMoney = (value: number) => zeroDecimalMoneyFormatter.format(value);
export const formatPercent = (value: number) => percentFormatter.format(value);
export const formatNumber = (value: number) => numberFormatter.format(value);
export const formatZeroDecimalNumber = (value: number) => zeroDecimalNumberFormatter.format(value);

export const formatDateWithTime = format('MMddyyhhmm');

export const formatUtcDateTimeToLocalDateTimeString = (dateParam: string | Date | undefined): string => {
    if (!dateParam) {
        return '';
    }

    const date = dateParam instanceof Date ? dateParam : new Date(`${dateParam}Z`);
    const dateString = `${date.toLocaleDateString()} ${date.toLocaleTimeString('en-US')}`;
    return dateString;
};

// MUI's `type: 'dateTime'` grid columns require the underlying (pre-format) cell value to be a real Date object.
export const parseUtcDateTime = (dateParam: string | Date | undefined): Date | null => {
    if (!dateParam) {
        return null;
    }
    return dateParam instanceof Date ? dateParam : new Date(`${dateParam}Z`);
};

export const formatLocalDateToLocalISODateTime = (dateParam: string | undefined): Date | null => {
    if (!dateParam) {
        return null;
    }

    const date = new Date(dateParam);
    date.setHours(0, 0, 0, 0);
    return date;
};
export const formatLocalDateToLocalISODateTimeString = (dateParam: string | undefined): string => {
    if (!dateParam) {
        return '';
    }

    const date = formatLocalDateToLocalISODateTime(dateParam);
    return date ? date.toISOString() : '';
};

export const formatDatePickerStringWithoutTime = (dateParam: string | Date | undefined): string => {
    if (!dateParam) {
        return '';
    }
    const date = dateParam instanceof Date ? dateParam : new Date(dateParam);
    return date.toLocaleDateString();
};

// convert date string such as "2025-12-01" to "12/01/2025" without time zone conversion
export const formatUtcDateStringWithoutTime = (dateParam: string | Date | undefined): string => {
    if (!dateParam) {
        return '';
    }
    if (dateParam instanceof Date) {
        return `${dateParam.getMonth() + 1}/${dateParam.getDate()}/${dateParam.getFullYear()}`;
    }
    const [year, month, day] = dateParam.split('-').map(Number);
    return `${month}/${day}/${year}`;
};

// MUI's `type: 'date'` grid columns require the underlying (pre-format) cell value to be a real Date object.
export const parseDatePickerDate = (dateParam: string | Date | undefined): Date | null => {
    if (!dateParam) {
        return null;
    }
    return dateParam instanceof Date ? dateParam : new Date(dateParam);
};

// Same as parseDatePickerDate, but parses "YYYY-MM-DD" as a local date (matching formatUtcDateStringWithoutTime)
// instead of UTC midnight, so the displayed day doesn't shift with the browser's timezone.
export const parseUtcDateOnly = (dateParam: string | Date | undefined): Date | null => {
    if (!dateParam) {
        return null;
    }
    if (dateParam instanceof Date) {
        return dateParam;
    }
    const [year, month, day] = dateParam.split('-').map(Number);
    return new Date(year, month - 1, day);
};

export const formatHtmlId = (text: string) => text.toLowerCase().replaceAll(/[^a-z0-9]+/gi, '-');

export const isValueEmpty = (value: any) => {
    return value === undefined || value === null || value === '';
};

export const determineEnduserDisplay = (city = '', state = '', street = '', endUserName = '', endusernumber = '') => {
    const address = [street, city, state].filter((part) => part.trim() !== '').join(', ');
    return [endUserName, address, endusernumber].filter((part) => part.trim() !== '').join(' | ');
};
